import { useState } from 'react';

import { ChevronDownIcon, DownloadIcon } from 'lucide-react';

import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@mds/shared/lib/api';
import { answerText } from '@mds/shared/lib/form-answers';
import { cn } from '@mds/shared/lib/utils';
import { FORM_RATING_MAX } from '@mds/shared/types/form-question';
import Select from '@mds/shared/ui/select';

import BarRanking from '@/components/charts/bar-ranking';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import Pager from '@/components/ui/pager';
import StatCard from '@/components/ui/stat-card';
import { Cell } from '@/components/ui/table';
import * as feedback from '@/events/feedback.api';
import { exportFeedbackXlsx } from '@/events/feedback-export';
import { stamp } from '@mds/shared/lib/dates';

const PAGE_SIZE = 20;

/**
 * What guests said after the event. Anonymous by design — the API returns no
 * attendee, ticket or contact detail — and the figures are the server's, so
 * the response rate counts every checked-in party, not just this page.
 */
export default function FeedbackResults({
  eventId,
  eventTitle,
}: {
  eventId: string;
  /** Titles the workbook and names the file. */
  eventTitle: string;
}) {
  const [page, setPage] = useState(1);
  const [direction, setDirection] = useState<'desc' | 'asc'>('desc');
  const [openId, setOpenId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const runExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await exportFeedbackXlsx(eventId, eventTitle);
    } catch (error) {
      setExportError(
        error instanceof ApiError ? error.message : 'The export could not be generated.',
      );
    } finally {
      setExporting(false);
    }
  };

  const list = useQuery({
    queryKey: ['feedback-responses', eventId, { page, direction }],
    queryFn: () => feedback.listResponses(eventId, { page, limit: PAGE_SIZE, direction }),
  });

  if (list.isPending) return <p className="m-0 text-base text-ink-muted">Loading responses…</p>;
  if (list.isError)
    return (
      <EmptyState
        title="Could not load responses"
        hint={
          list.error instanceof ApiError ? list.error.message : 'The server could not be reached.'
        }
      />
    );

  const { summary, data: rows, pagination } = list.data;
  const distribution = Array.from({ length: FORM_RATING_MAX }, (_, i) => {
    const rating = FORM_RATING_MAX - i;
    const count = summary.ratingDistribution[String(rating)] ?? 0;
    return {
      key: String(rating),
      label: `${rating} / ${FORM_RATING_MAX}`,
      value: count,
      display: String(count),
    };
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Responses"
          value={String(summary.totalResponses)}
          note={`from ${summary.checkedInRegistrations} checked-in ${
            summary.checkedInRegistrations === 1 ? 'party' : 'parties'
          }`}
          density="compact"
        />
        <StatCard
          label="Response rate"
          value={`${Math.round(summary.responseRatePercent)}%`}
          note="of the parties invited"
          density="compact"
        />
        <StatCard
          label="Average rating"
          value={
            summary.averageOverallRating === null ? '–' : summary.averageOverallRating.toFixed(1)
          }
          note={`out of ${FORM_RATING_MAX}`}
          density="compact"
        />
      </div>

      {summary.totalResponses === 0 ? (
        <EmptyState
          title="No responses yet"
          hint="Invitations go out when the event ends, to guests who checked in and left an email."
        />
      ) : (
        <>
          <Card title="Overall rating">
            <BarRanking rows={distribution} />
          </Card>

          <Card
            title="Responses"
            action={
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="quiet" disabled={exporting} onClick={runExport}>
                  <DownloadIcon className="size-5" aria-hidden="true" />
                  {exporting ? 'Preparing…' : 'Export Excel'}
                </Button>
                <Select
                  value={direction}
                  onChange={(v) => {
                    setDirection(v as 'desc' | 'asc');
                    setPage(1);
                    setOpenId(null);
                  }}
                  options={[
                    { value: 'desc', label: 'Newest first' },
                    { value: 'asc', label: 'Oldest first' },
                  ]}
                  aria-label="Order responses"
                />
              </div>
            }
          >
            {exportError && <p className="m-0 mb-3 text-base text-warn-text">{exportError}</p>}
            {/* Not the shared Table: its minimum width is wider than the event modal. */}
            <table className="w-full border-collapse text-left text-base">
              <thead>
                <tr>
                  {['Submitted', 'Rating', 'Comment', 'Answers'].map((h) => (
                    <th
                      key={h}
                      className={cn(
                        'border-b-2 border-hairline pr-4 pb-3 font-mono text-sm font-semibold tracking-wide text-ink-secondary uppercase',
                        h === 'Comment' && 'hidden sm:table-cell',
                      )}
                    >
                      {h}
                    </th>
                  ))}
                  <th className="border-b-2 border-hairline pb-3">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <ResponseRow
                    key={r.id}
                    response={r}
                    open={openId === r.id}
                    onToggle={() => setOpenId((id) => (id === r.id ? null : r.id))}
                  />
                ))}
              </tbody>
            </table>
            <Pager
              pagination={pagination}
              onPage={(p) => {
                setPage(p);
                setOpenId(null);
              }}
            />
          </Card>
        </>
      )}
    </div>
  );
}

type FeedbackResponse = Awaited<ReturnType<typeof feedback.listResponses>>['data'][number];

/**
 * One response as a summary row that opens, in place, onto the guest's comment
 * and every question with its answer. The whole row toggles; the chevron is
 * the keyboard target.
 */
function ResponseRow({
  response: r,
  open,
  onToggle,
}: {
  response: FeedbackResponse;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `feedback-response-${r.id}`;
  const answered = r.answers.filter((a) => answerText(a.value)).length;

  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          'cursor-pointer border-b border-hairline/70 transition-colors hover:bg-brand-wash',
          open && 'border-b-0 bg-brand-wash',
        )}
      >
        <Cell className="font-mono whitespace-nowrap text-ink-secondary">
          {stamp(r.submittedAt)}
        </Cell>
        <Cell>
          <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
            {r.overallRating} / {FORM_RATING_MAX}
          </Chip>
        </Cell>
        <Cell className="hidden w-full max-w-0 sm:table-cell">
          {r.comment ? (
            <span className="block truncate">{r.comment}</span>
          ) : (
            <span className="text-ink-muted">No comment</span>
          )}
        </Cell>
        <Cell className="whitespace-nowrap text-ink-secondary">
          {answered} of {r.answers.length}
        </Cell>
        <Cell className="pr-0 text-right">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={open ? 'Hide response' : 'Show response'}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="inline-flex min-h-(--target-min) min-w-(--target-min) items-center justify-center rounded-xl text-ink-muted hover:text-brand-strong focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
          >
            <ChevronDownIcon
              aria-hidden
              className={cn('size-5 transition-transform', open && 'rotate-180')}
            />
          </button>
        </Cell>
      </tr>
      {open && (
        <tr id={panelId} className="border-b border-hairline/70 bg-brand-wash">
          <td colSpan={5} className="px-3 pb-4">
            <div className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-5">
              <section className="flex flex-col gap-1.5">
                <h4 className="m-0 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
                  Comment
                </h4>
                {r.comment ? (
                  <p className="m-0 text-base whitespace-pre-line">{r.comment}</p>
                ) : (
                  <p className="m-0 text-base text-ink-muted">The guest left no comment.</p>
                )}
              </section>

              {r.answers.length > 0 && (
                <section className="flex flex-col gap-1.5 border-t border-hairline pt-4">
                  <h4 className="m-0 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
                    Questions
                  </h4>
                  <ol className="m-0 flex list-none flex-col p-0">
                    {r.answers.map((a, i) => {
                      const text = answerText(a.value);
                      return (
                        <li
                          key={a.questionId}
                          className="grid gap-x-6 gap-y-1 border-b border-hairline/70 py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]"
                        >
                          <span className="flex gap-3 text-ink-secondary">
                            <span className="font-mono text-ink-muted">{i + 1}.</span>
                            {a.label}
                          </span>
                          <span
                            className={cn(
                              'break-words whitespace-pre-line',
                              text ? 'font-semibold' : 'text-ink-muted',
                            )}
                          >
                            {text || 'Not answered'}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
